import { OnDestroy } from '@angular/core';
import {
    IProcess,
    IQueue,
    IRobot,
} from '@models';
import { FOLDER_PATH_HEADER_NAME } from '@pages/monitoring/monitoring.interceptor';
import { ODataService } from '@services/generics/odata.service';

import {
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    map,
    share,
    takeUntil,
    tap,
} from 'rxjs/operators';

export type DashboardEntityType = IRobot | IQueue | IProcess;
export interface IDashboardEntity {
    id: number;
    name: string;
    fqn?: string;
}

export abstract class NameCacheService<K extends DashboardEntityType = DashboardEntityType> implements OnDestroy {
    protected _propertyName: keyof K = 'name';
    protected _cache: Record<number, Observable<string>> = {};

    private _destroyed$ = new Subject();

    constructor(
        protected _entityService: ODataService<K, number>,
    ) { }

    ngOnDestroy() {
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public get(id: number, { default$, fqn }: { default$?: Observable<string>; fqn?: string } = {}): Observable<string> {
        if (this._cache[id] == null) {
            this._cache[id] = default$ || this._entityService
                .getInstance(id, null, { [FOLDER_PATH_HEADER_NAME]: fqn })
                .pipe(
                    tap(entity => this.cacheEntity(entity)),
                    map(entity => `${entity[this._propertyName]}`),
                    share(),
                    takeUntil(this._destroyed$),
                );
        }
        return this._cache[id];
    }

    public cacheEntity(entity: IDashboardEntity) {
        this._cache[entity.id] = of(`${entity.name}`);
    }
}
